import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SimpleForm, SelectInput, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import useOffboardReasons from './hooks/useOffboardReasons';
import { useOffboardValidations } from './hooks/useOffboardValidations';
import useLocationOffboard from './hooks/useLocationOffboard';
import { useParams } from 'react-router';
import { CircularProgress } from '@mui/material';

const ConfirmationModal = ({ isOpen, message, onConfirm, onCancel }: any) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#bbbbbb',
          padding: '20px',
          borderRadius: '8px',
          width: '400px',
          textAlign: 'center',
        }}
      >
        <p>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px' }}>
          <button style={{ padding: '10px 20px' }} onClick={onCancel}>
            Cancel
          </button>
          <button style={{ padding: '10px 20px' }} onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const Offboard = () => {
  const notify = useNotify();
  const fetchOffboardReasons = useOffboardReasons();
  const { id } = useParams();
  const fetchOffboardValidations = useOffboardValidations();
  const { offboardLocation, loading } = useLocationOffboard();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'reason_only' | 'offboard' | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const [validations, setValidations] = useState<any[]>([]);
  const [allValidationsPass, setAllValidationsPass] = useState(false);
  const [isValidationsLoading, setIsValidationsLoading] = useState(true);
  const [location, setLocation] = useState<{ id: number; name: string; status: string } | null>(
    null,
  );

  const hasFetchedValidations = useRef(false);

  const fetchValidations = useCallback(async () => {
    if (!id || hasFetchedValidations.current) {
      return;
    }

    hasFetchedValidations.current = true;

    try {
      const data = await fetchOffboardValidations(Number(id));
      setValidations(data.validations || []);
      setAllValidationsPass(data.validationPassed);
      setLocation(data.location || null);
    } catch (error) {
      console.error('Error fetching offboard validations:', error);
      notify('Failed to fetch offboard validations', { type: 'error' });
    } finally {
      setIsValidationsLoading(false);
    }
  }, [fetchOffboardValidations, id, notify]);

  useEffect(() => {
    fetchValidations();
  }, [fetchValidations]);
  const [reasons, setReasons] = useState<{ value: string; label: string }[]>([]);
  const [isReasonsLoading, setIsReasonsLoading] = useState(true);

  const fetchReasons = React.useCallback(async () => {
    if (!location) {
      return;
    }

    try {
      const data = await fetchOffboardReasons(location.id);
      setReasons(data);
      setIsReasonsLoading(false);
    } catch (error) {
      notify('Failed to fetch offboard reasons', { type: 'error' });
    }
  }, [fetchOffboardReasons, location?.id, notify]);

  useEffect(() => {
    if (!isReasonsLoading) {
      return;
    }
    fetchReasons();
  }, [fetchReasons, isReasonsLoading]);

  const handleCancel = () => navigate(-1);

  const handleAction = async (action: 'reason_only' | 'offboard') => {
    if (!selectedReason) {
      notify('Please select an offboard reason before proceeding.', { type: 'error' });
      return;
    }

    try {
      const response = await offboardLocation({
        locationId: parseInt(id, 10),
        offboardReason: selectedReason,
        action,
        onError: (errorMessage) => {
          notify(errorMessage, { type: 'error' });
        },
      });

      if (response?.action) {
        const successMessage =
          action === 'reason_only' ? 'Offboard reason saved' : 'Location offboarded';
        notify(successMessage, { type: 'success' });
        setTimeout(() => {
          navigate(`/Location/${id}/show`);
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleSaveReasonOnlyClick = () => {
    if (!selectedReason) {
      notify('Please select an offboard reason before proceeding.', { type: 'error' });
      return;
    }
    setModalType('reason_only');
    setIsModalOpen(true);
  };

  const handleOffboardNowClick = () => {
    if (!selectedReason) {
      notify('Please select an offboard reason before proceeding.', { type: 'error' });
      return;
    }
    setModalType('offboard');
    setIsModalOpen(true);
  };

  if (isReasonsLoading || isValidationsLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <SimpleForm toolbar={null}>
        <SelectInput
          label="Offboard Reason"
          source="offboard"
          choices={reasons}
          optionValue="value"
          optionText="label"
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            setSelectedReason(event.target.value)
          }
        />
        <div>
          <h3>Checking for offboard prerequisites:</h3>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', textAlign: 'left', padding: '8px' }}>
                  Condition
                </th>
                <th style={{ border: '1px solid #ddd', textAlign: 'left', padding: '8px' }}>
                  Meet
                </th>
              </tr>
            </thead>
            <tbody>
              {validations.map((validation) => (
                <tr key={validation.key}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{validation.label}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {validation.passed ? 'Yes' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontWeight: 'bold' }}>
            Overall Status:{' '}
            {allValidationsPass ? 'All validations passed' : 'Some validations failed'}
          </p>
          {allValidationsPass && (
            <>
              <p>Active accounts that only associate with this location will be deactivated</p>
              <p>
                Active Billing Groups that only associate with this location will be deactivated
              </p>
              <p>
                Active Location Groups that only associate with this location will be deactivated
              </p>
            </>
          )}
          {!allValidationsPass && (
            <>
              <p>
                We can only proceed with the location offboard request when all conditions are met,
                currently you can only save the offboard reason.
              </p>
              <p>
                Once all conditions are met, you can proceed with the location offboard process
                again.
              </p>
              <p>Do you want to save the offboard reason now?</p>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="button" style={{ padding: '10px 20px' }} onClick={handleCancel}>
            Cancel
          </button>
          <button
            type="button"
            style={{ padding: '10px 20px' }}
            onClick={handleSaveReasonOnlyClick}
            disabled={loading || !selectedReason}
          >
            {loading ? 'Saving...' : 'Save offboard reason only'}
          </button>
          {allValidationsPass && (
            <button
              type="button"
              style={{ padding: '10px 20px' }}
              onClick={handleOffboardNowClick}
              disabled={loading || !selectedReason}
            >
              {loading ? 'Offboarding...' : 'Offboard now'}
            </button>
          )}
        </div>
      </SimpleForm>

      <ConfirmationModal
        isOpen={isModalOpen}
        message={
          modalType === 'reason_only'
            ? `Are you sure you want to save the offboard reason for location ${location?.name}?`
            : `Are you sure you want to offboard location ${location?.name}?`
        }
        onConfirm={() => handleAction(modalType)}
        onCancel={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Offboard;
